import axios, { AxiosError } from "axios";
import { ENDPOINT } from "./endpoint";
import store from "@/utils/store";
import { CreateUserInfo, PatientExtraDefinitionValue, PatientInfo, ResetPassword, ResetPasswordRequest, RoleBaseInfo, RoleInfo, RolePermission, RoleWithCenterPermissions, UpdatePassword, UserInfo } from "@/utils/models/user.model";
import { CenterBaseInfo, CenterInfo } from "@/utils/models/center.model";
import DateFormatUtils from "@/utils/methods/date_formats";
import stateStore from "@/utils/store";
import { Consent, ConsentAction } from "@/utils/models/consent.model";
import { ContentBlock, ContentCategory, ContentInfo } from "@/utils/models/content.model";
import { Event } from "@/utils/models/event.model";
import { bool_to_int } from "@/utils/methods/int_to_bool";
import { TherapiesStatisticsRangeType, TherapyStatistics } from "@/utils/models/therapy.model";
import { RequestSupportForm } from "@/utils/methods/form_utils";
import { Survey, SurveyStep } from "@/utils/models/survey.model";
import { OrderField } from "@/utils/models/_model";
import { Zone } from "@/utils/models/zone.model";
import { i18n } from "@/assets/i18n/i18n";

axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';

axios.interceptors.request.use(
    (config) => {
        const user = store.user;
        if (user !== null) {
            config.headers.Authorization = `${user.token.token_type} ${user.token.access_token}`;
        }
        return config;
    },

    (error) => {
        return Promise.reject(error);
    }
);
axios.interceptors.response.use(
    (response) => {
        if ('success' in response.data && response.data.success === false) {
            console.error('API_SUCCESS', response.data.success)
            return Promise.reject(response);
        } else {
            return response;
        }
    },
    (error: AxiosError) => {
        console.error('API_ERROR', error);
        const url = new URL(error.config?.url ?? '');
        if (error.response?.status === 401) {
            _logout(url);
        } else if (error.response?.status === 403) {
            _logout(url);
        }
        throw error;
    },
);

const _logout = (url: URL) => {
    stateStore.logout();
    console.warn(url.pathname);
    if (url.pathname !== `${ENDPOINT.API}${ENDPOINT.LOGIN}` && url.pathname !== `${ENDPOINT.API}${ENDPOINT.LOGIN}${ENDPOINT.OTP}${ENDPOINT.VERIFY}`) {
        location.href = '/';
    }
}

const API = {
    /* AUTH */
    login(username: string, password: string) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.LOGIN}`, {
            email: username,
            password: password,
        }, {
            params: {
                lang: i18n.global.locale
            }
        });
    },
    loginOTP(username: string) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.LOGIN}${ENDPOINT.OTP}`, {
            email: username,
        }, {
            params: {
                lang: i18n.global.locale
            }
        });
    },
    verifyOTP(challenge_token: string, code: string) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.LOGIN}${ENDPOINT.OTP}${ENDPOINT.VERIFY}`, {
            challenge_token: challenge_token,
            code: code,
        }, {
            params: {
                lang: i18n.global.locale
            }
        });
    },
    verifyOTPInvitation(params: {token: string, challenge_token: string, code: string}) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INVITATIONS}/${params.token}${ENDPOINT.OTP}${ENDPOINT.VERIFY}`, {
            challenge_token: params.challenge_token,
            code: params.code,
        }, {
            params: {
                lang: i18n.global.locale
            }
        });
    },
    logout() {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.LOGOUT}`)
    },
    /* ME */
    introspection() {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INTROSPECTION}`)
    },
    setLanguage(locale: string) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INTROSPECTION}${ENDPOINT.LANGUAGE}`, {
            language_code: locale
        })
    },
    deleteAccount() {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INTROSPECTION}${ENDPOINT.PRIVACY}${ENDPOINT.RIGHT_TO_ERASURE}`)
    },

    async getUserStats(center_id?: number) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}${ENDPOINT.STATS}`, {
            params: {
                center_id: center_id
            }
        });
    },
    async resetPasswordRequest(request: ResetPasswordRequest) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.RESET_PASSWORD_REQUEST}`, request);
    },
    async resetPassword(params: ResetPassword) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.RESET_PASSWORD}`, params);
    },
    async updatePassword(params: UpdatePassword) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.UPDATE_PASSWORD}`, params);
    },
    /* CENTERS */
    async getCenters(params?: { page?: number, zone_id?: string | number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CENTERS}`, {
            params: {
                page: params?.page,
                zone_id: params?.zone_id
            }
        });
    },
    async getCenter(id: number | string) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CENTERS}/${id}`);
    },
    async createCenter(center: CenterBaseInfo) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CENTERS}`, center);
    },
    async updateCenter(center: CenterInfo) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CENTERS}/${center.id!}`, center);
    },
    async deleteCenter(id: number | string) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CENTERS}/${id}`);
    },
    async getCenterStats(id: number | string) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CENTERS}/${id}${ENDPOINT.STATS}`);
    },
    async getCenterHCPs(params: { center_id: number | string, page: number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CENTERS}/${params.center_id}${ENDPOINT.LIST_HCPS}`, {
            params: {
                page: params.page
            }
        });
    },
    /* PATIENTS */
    async getPatient(id: number | string) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${id}`);
    },
    async createPatient(newPatient: PatientInfo) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}`, newPatient);
    },
    async updatePatient(patient: PatientInfo) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${patient.id!}`, patient);
    },
    async deletePatient(id: string) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${id}`);
    },
    async getPatients(params: { page: number, center_id?: number, email?: string, query?: string, right_to_erasure_requested?: boolean }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}`, {
            params: {
                page: params.page,
                center_id: params.center_id,
                email: params.email,
                search_str: params.query,
                right_to_erasure: params.right_to_erasure_requested
            }
        });
    },
    async searchPatients(query: string) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}${ENDPOINT.SEARCH}`, {
            params: {
                str: query
            }
        });
    },

    async downloadPatientData(params: { patient_id: string | number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PRIVACY}${ENDPOINT.PORTABILITY}/${params.patient_id}`);
    },


    /* CAREGIVERS */
    async addCaregiver(params: { patient_id: string | number, caregiver: UserInfo }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}/${ENDPOINT.USER}`, {
            send_mail: true,
            caregiver_account: true,
            ...params.caregiver
        });
    },
    async updateCaregiver(params: { patient_id: string | number, caregiver: UserInfo }) {
        throw Error('API not implemented');
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.USER}/${params.caregiver.id}`, params.caregiver);
    },
    async deleteCaregiver(params: { patient_id: string | number, caregiver_id: string | number }) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}/${params.caregiver_id}`);
    },
    /* USERS */
    async getUsers(params: { page: number, role_id?: number, role_ids?: number[] }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.USERS}`, {
            params: {
                page: params.page,
                role_id: params.role_id,
                role_ids: params.role_ids?.join(',')
            }
        });
    },
    async getUser(id: number | string) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.USERS}/${id}`);
    },
    async createUser(newUser: CreateUserInfo) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.USERS}`, newUser);
    },
    async updateUser(user: UserInfo) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.USERS}/${user.id!}`, user);
    },
    async deleteUser(id: string) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.USERS}/${id}`);
    },
    async recoveryUserPassword(id: string) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.USERS}/${id}${ENDPOINT.RECOVERY_PASSWORD}`);
    },
    /* THERAPIES */
    async getTherapies(patientId: string, params?: { date: Date, is_prn?: boolean }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${patientId}${ENDPOINT.THERAPIES}`, {
            params: {
                date: params?.date != null ? DateFormatUtils.YYYYMMDD(params.date) : undefined,
                is_prn: params?.date != null ? params.is_prn : undefined
            }
        });
    },
    async scheduleTherapy(params: { patient_id: string, start: Date, end: Date, therapy_scheme_id: string, is_prn: boolean, dosage?: string }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.THERAPIES}`, {
            start_at: DateFormatUtils.YYYYMMDD(params.start),
            stop_at: DateFormatUtils.YYYYMMDD(params.end),
            therapy_scheme_id: params.therapy_scheme_id,
            is_prn: params.is_prn,
            dosage: params.dosage
        });
    },
    async getIntakes(params: { patient_id: string, therapy_id?: string, date?: Date }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.THERAPIES}${params.therapy_id ? `/${params.therapy_id}` : ``}${ENDPOINT.INTAKES}`, {
            params: {
                date: params.date != null ? DateFormatUtils.YYYYMMDD(params.date) : undefined
            }
        });
    },
    async getIntakeStats(params: { patient_id: string, therapy_id?: string, date?: Date }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.THERAPIES}${params.therapy_id ? `/${params.therapy_id}` : ``}${ENDPOINT.INTAKES}/${ENDPOINT.STATS}`, {
            params: {
                date: params.date != null ? DateFormatUtils.YYYYMMDD(params.date) : undefined
            }
        });
    },
    async stopTherapy(params: { patient_id: string, therapy_id: string, stop_at: Date }) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.THERAPIES}/${params.therapy_id}${ENDPOINT.STOP}`, {
            stop_at: DateFormatUtils.YYYYMMDD(params.stop_at!)
        });
    },
    async deleteTherapy(params: { patient_id: string, therapy_id: string }) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.THERAPIES}/${params.therapy_id}`);
    },
    /* THERAPY_SCHEME */
    async getTherapySchemes(page?: number) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.THERAPY_SCHEME}`, {
            params: {
                page: page
            }
        });
    },
    async getTherapyScheme(id: string) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.THERAPY_SCHEME}/${id}`);
    },
    async getTherapyTrend(params: { patient_id: string, therapy_id?: string | number, date_from: Date | string, date_to: Date | string, range_type?: TherapiesStatisticsRangeType, show_prn?: boolean }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.THERAPIES}${ENDPOINT.THERAPY_TREND}`, {
            params: {
                range_type: params.range_type ?? 'week',
                therapy_id: params.therapy_id,
                date_from: params.date_from,
                date_to: params.date_to,
                show_prn: params.show_prn
            }
        });
    },
    /* SURVEYS */
    async getSurveys(params?: { page?: number, per_page?: number, load_steps?: boolean }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.SURVEYS}`, {
            params: {
                page: params?.page,
                per_page: params?.per_page,
                load_steps: params?.load_steps != undefined ? bool_to_int(params?.load_steps) : undefined
            }
        });
    },
    async createSurvey(survey: Survey) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.SURVEYS}`, {
            ...survey,
            title: survey.title?.[i18n.global.locale],
            description: survey.description?.[i18n.global.locale],
            footer_text: survey.footer_text?.[i18n.global.locale]
        });
    },
    async updateSurvey(survey: Survey) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.SURVEYS}/${survey.id!}`, survey);
    },
    async updateSurveyLocalization(survey: Survey, lang: string) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.SURVEYS}/${survey.id!}/${lang}`, {
            title: survey.title?.[lang],
            description: survey.description?.[lang],
            footer_text: survey.footer_text?.[lang]
        });
    },
    async deleteSurvey(id: string | number) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.SURVEYS}/${id}`);
    },

    async createSurveyStep(params: { survey_id: string | number, step: SurveyStep }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.SURVEYS}/${params.survey_id}${ENDPOINT.STEPS}`, {
            ...params.step,
            title: params.step.title?.[i18n.global.locale],
            text: params.step.text?.[i18n.global.locale],
            body: params.step.body?.[i18n.global.locale],
            choices: params.step.choices?.[i18n.global.locale]
        });
    },
    async updateSurveyStep(step: SurveyStep) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.SURVEY_STEPS}/${step.id}`, step);
    },
    async updateSurveyStepLocalization(step: SurveyStep, lang: string) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.SURVEY_STEPS}/${step.id!}/${lang}`, {
            title: step.title?.[lang],
            text: step.text?.[lang],
            body: step.body?.[lang],
            choices: step.choices?.[lang],
        });
    },
    async orderSurveySteps(params: { step_id: string | number, step_order: OrderField[] }) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.SURVEY_STEPS}/${params.step_id}${ENDPOINT.CONTENT_BLOCKS}`, params.step_order);
    },

    async getSurveysPriority(params: { patient_Id: string | undefined, page?: number, per_page?: number, load_steps?: boolean }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_Id}${ENDPOINT.SURVEYS}`, {
            params: {
                page: params?.page,
                per_page: params?.per_page,
                load_steps: params?.load_steps != undefined ? bool_to_int(params?.load_steps) : undefined
            }
        });
    },
    async getSurvey(id: string, lang?: string) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.SURVEYS}/${id}`, {
            params: {
                lang: lang ?? i18n.global.locale
            }
        });
    },
    async getSurveyAnswers(params: { survey_id: string | number, patient_id: string, page?: number, date_from?: Date, date_to?: Date, date?: Date, order?: 'asc' | 'desc' }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.SURVEYS}/${params.survey_id}${ENDPOINT.RESPONSES}`, {
            params: {
                page: params.page,
                date: params?.date != null ? DateFormatUtils.YYYYMMDD(params.date) : undefined,
                date_from: params?.date_from != null ? DateFormatUtils.YYYYMMDD(params.date_from) : undefined,
                date_to: params?.date_to != null ? DateFormatUtils.YYYYMMDD(params.date_to) : undefined,
                order: params.order
            }
        });
    },
    async getSurveySettings(params: { patient_Id: any, survey_Id: string | number, center_Id: number, delay: number }) {

        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_Id}/${params.survey_Id}/settings/hassurveysettings`, {
            timeout: params.delay,
            params: {
                center_id: params?.center_Id,
            },

        })

    },

    async setSurveySettings(params: { patient_Id: any, survey_Id: string | number, center_Id: number, isPrioritary: boolean }) {

        let dataObj = {
            data: params.isPrioritary
        }

        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_Id}/${params.survey_Id}/settings`, dataObj, {
            params: {
                center_id: params?.center_Id,
            }
        })

    },

    /* ROLES */
    async getRoles(params?: { page?: number, ids?: string | number[], zone_id?: string | number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ROLES}`, { params: { page: params?.page, ids: params?.ids, zone_id: params?.zone_id } });
    },
    async createRole(role: RoleBaseInfo) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ROLES}`, role);
    },
    async updateRole(role: RoleInfo) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ROLES}/${role.id}`, role);
    },
    async getRole(id: string | number) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ROLES}/${id}`);
    },
    async deleteRole(id: string | number) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ROLES}/${id}`);
    },
    async getRolePermissions(id: string | number) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ROLES}/${id}${ENDPOINT.PERMISSIONS}`);
    },
    async getCenterPermissions(id: string | number) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CENTERS}/${id}${ENDPOINT.PERMISSIONS}`);
    },
    async updateRolePermissions(permissions: RolePermission) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ROLES}/${permissions.role_id}${ENDPOINT.PERMISSIONS}`, permissions);
    },
    /* CONTENT_CATEGORIES */
    async getContentCategories(page?: number) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENT_CATEGORIES}`, {
            params: {
                page: page
            }
        });
    },
    async getContentCategory(id: string | number) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENT_CATEGORIES}/${id}`, {
            params: {
                show_all_translations: true,
            }
        });
    },
    async createContentCategory(category: ContentCategory) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENT_CATEGORIES}`, category);
    },
    async createContentCategoryLocalization(category: ContentCategory) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENT_CATEGORIES}`, {
            ...category,
            name: category.name?.[i18n.global.locale],
            description: category.description?.[i18n.global.locale]
        });
    },
    async updateContentCategory(category: ContentCategory) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENT_CATEGORIES}/${category.id!}`, category);
    },
    async updateContentCategoryLocalization(category: ContentCategory, lang: string) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENT_CATEGORIES}/${category.id!}/${lang}`, {
            name: category.name?.[lang],
            description: category.description?.[lang]
        });
    },
    async deleteContentCategory(id: string | number) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENT_CATEGORIES}/${id}`);
    },
    /* CONTENTS */
    async getContents(params?: { page?: number, category_id?: number | string }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}`, {
            params: {
                page: params?.page,
                content_category_id: params?.category_id,
            }
        });
    },
    async getContent(id: string | number) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${id}`, {
            params: {
                show_all_translations: true
            }
        });
    },
    async createContent(content: ContentInfo) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}`, content);
    },
    async createContentLocalization(content: ContentInfo) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}`, {
            ...content,
            title: content.title?.[i18n.global.locale],
            description: content.description?.[i18n.global.locale]
        });
    },
    async updateContent(content: ContentInfo) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${content.id!}`, content);
    },
    async updateContentLocalization(content: ContentInfo, lang: string) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${content.id!}/${lang}`, {
            title: content.title?.[lang],
            description: content.description?.[lang]
        });
    },
    async deleteContent(id: string | number) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${id}`);
    },
    async setActiveContent(params: { content_id: string | number, active: 0 | 1 }) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${params.content_id}${ENDPOINT.ACTIVE}/${params.active}`);
    },
    /* CONTENT_BLOCKS */
    async createContentBlock(params: { content_id: string | number, block: ContentBlock }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${params.content_id}${ENDPOINT.CONTENT_BLOCKS}`, params.block);
    },
    async createContentBlockLocalization(params: { content_id: string | number, block: ContentBlock, order?: number }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${params.content_id}${ENDPOINT.CONTENT_BLOCKS}`, {
            ...params.block,
            subtitle: params.block.subtitle?.[i18n.global.locale],
            value: params.block.value?.[i18n.global.locale],
            order: params.order,
        });
    },
    async updateContentBlock(params: { content_id: string | number, block: ContentBlock }) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${params.content_id}${ENDPOINT.CONTENT_BLOCKS}/${params.block.id!}`, params.block);
    },
    async updateContentBlockLocalization(params: { lang: string, content_id: string | number, block: ContentBlock }) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${params.content_id}${ENDPOINT.CONTENT_BLOCKS}/${params.block.id!}/${params.lang}`, {
            subtitle: params.block?.subtitle?.[params.lang],
            value: params.block?.value?.[params.lang],
            type: params.block?.type
        });
    },
    async deleteContentBlock(params: { content_id: string | number, block: ContentBlock }) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${params.content_id}${ENDPOINT.CONTENT_BLOCKS}/${params.block.id!}`);
    },
    async orderContentBlocks(params: { content_id: string | number, blocks_order: OrderField[] }) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONTENTS}/${params.content_id}${ENDPOINT.CONTENT_BLOCKS}${ENDPOINT.ORDER}`, params.blocks_order);
    },
    /* CONTENT_PERMISSION */
    async getPatientContentCategories(params: { patient_id: string | number, page?: number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.CONTENT_CATEGORIES}`, {
            params: {
                page: params.page
            }
        });
    },
    async setPatientContentCategoryPermission(params: { patient_id: string | number, content_category_id?: number | string }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.CONTENT_CATEGORY_PERMISSION}`, {
            content_category_id: params.content_category_id?.toString()
        });
    },
    async deletePatientContentCategoryPermission(params: { patient_id: string | number, content_category_id?: number | string }) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.CONTENT_CATEGORY_PERMISSION}/${params.content_category_id}`);
    },
    /* MESSAGES */
    async getMessages(params: { patient_id: string | number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.HCP_MESSAGES}`);
    },
    async sendMessage(params: { patient_id: string | number, subject: string, body: string }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.HCP_MESSAGES}`, {
            subject: params.subject,
            body: params.body
        });
    },
    async deleteMessage(params: { message_id: string | number }) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.HCP_MESSAGES}/${params.message_id}`);
    },
    /* EVENTS */
    async getEvents(params?: { date?: Date, patientId?: string, date_from?: Date, date_to?: Date }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.EVENTS}`, {
            params: {
                date: params?.date != null ? DateFormatUtils.YYYYMMDD(params.date) : undefined,
                date_from: params?.date_from != null ? DateFormatUtils.YYYYMMDD(params.date_from) : undefined,
                date_to: params?.date_to != null ? DateFormatUtils.YYYYMMDD(params.date_to) : undefined,
                patient_id: params?.patientId != null ? params.patientId : undefined
            }
        });
    },
    async getEvent(params: { event_id: string | number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.EVENTS}/${params.event_id}`);
    },
    async deleteEvent(params: { event_id: string | number }) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.EVENTS}/${params.event_id}`);
    },
    async createEvent(params: { patient_id: string | number, event: Event }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.PATIENTS}/${params.patient_id}${ENDPOINT.EVENTS}`, params.event);
    },
    async updateEvent(params: { event: Event }) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.EVENTS}/${params.event.id}`, params.event);
    },
    async getEventTypes() {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.EVENT_TYPES}`);
    },
    /* CONSENT */
    async getConsents(params?: { page?: number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONSENTS}`, {
            params: {
                page: params?.page,
            }
        });
    },
    async getConsent(id: string | number) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONSENTS}/${id}`);
    },
    async getConsentVersions(id: string | number) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONSENTS}/${id}${ENDPOINT.VERSIONS}`);
    },
    async getConsentVersion(params: { consent_id: string | number, version_id: string | number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONSENTS}/${params.consent_id}${ENDPOINT.VERSIONS}/${params.version_id}`);
    },
    async createConsent(consent: Consent) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONSENTS}`, consent);
    },
    async updateConsent(consent: Consent) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONSENTS}/${consent.id!}`, consent);
    },
    async deleteConsent(id: string | number) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONSENTS}/${id}`);
    },
    /* ROLE CONSENT */
    async getConsentRoles(params: { consent_id: string | number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONSENTS}/${params.consent_id}${ENDPOINT.ROLES}`, {
            data: {
                consent_id: params.consent_id
            }
        });
    },
    async storeConsentRole(params: { consent_id: string | number, role_id: string | number }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONSENTS}/${params.consent_id}${ENDPOINT.ROLES}`, {
            role_id: params.role_id
        });
    },
    async deleteConsentRole(params: { consent_id: string | number, role_id: string | number }) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.CONSENTS}/${params.consent_id}${ENDPOINT.ROLES}/${params.role_id}`);
    },
    /* USER CONSENT */
    async getUserConsents(params?: { page?: number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INTROSPECTION}${ENDPOINT.CONSENTS}`, {
            params: {
                page: params?.page,
            }
        });
    },
    async registerUserConsent(params: { version_id: string | number, action: ConsentAction }) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INTROSPECTION}${ENDPOINT.USER_CONSENT_VERSION}/${params.version_id}`, {
            action: params.action,
        });
    },
    /* DATA EXTRA */
    async getExtraDefinitions(entity: ExtraDefinitionEntity, params?: { lang?: string }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}/${entity}${ENDPOINT.EXTRA_DEFINITIONS}`, {
            params: {
                lang: params?.lang != null ? params.lang : undefined,
            }
        });
    },
    async getExtraDefinitionValues(params: { entity: ExtraDefinitionEntity, entity_id: string | number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}/${params.entity}/${params.entity_id}${ENDPOINT.EXTRA_DEFINITION_VALUES}`);
    },
    async createExtraDefinitionValues(params: { entity: ExtraDefinitionEntity, entity_id: string | number, definition_values: PatientExtraDefinitionValue }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}/${params.entity}/${params.entity_id}${ENDPOINT.EXTRA_DATA}`, params.definition_values);
    },
    async updateExtraDefinitionValues(params: { entity: ExtraDefinitionEntity, entity_id: string | number, definition_values: PatientExtraDefinitionValue }) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}/${params.entity}/${params.entity_id}${ENDPOINT.EXTRA_DATA}`, params.definition_values);
    },
    /* STATISTICS */
    async getStatsCenters() {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.STATISTICS}${ENDPOINT.CENTERS}`);
    },
    async getStatsUsers() {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.STATISTICS}${ENDPOINT.USERS}`);
    },
    async getStatsPatients() {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.STATISTICS}${ENDPOINT.PATIENTS}`);
    },
    /* LABELS */
    async getLabels(params?: { label?: string, lang?: string }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.EXTERNAL_API}${ENDPOINT.LABELS}`, {
            params: {
                type: params?.label != null ? params.label : undefined,
                lang: params?.lang != null ? params.lang : undefined
            }
        });
    },
    /* SUPPORT */
    async getSupport(form: RequestSupportForm) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.EXTERNAL_API}${ENDPOINT.SUPPORT}`, form
        );
    },

    /* INVITATION */
    async sendInvite(params: { email: string }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INVITATIONS}`, { email: params.email })
    },
    async getInviteToken(params: { token: string, lang?: string }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INVITATIONS}/${params.token}`, {
            params: {
                lang: i18n.global.locale
            }
        })
    },
    async acceptInvite(params: { token: string, invited_user: CreateUserInfo, terms_of_use_accepted: boolean }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INVITATIONS}/${params.token}${ENDPOINT.ACCEPT}`, {...params.invited_user, terms_of_use_accepted: params.terms_of_use_accepted,   });
    },
    async acceptOTPInvite(params: { token: string, email: string }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INVITATIONS}/${params.token}${ENDPOINT.OTP}`, {
            email: params.email
        });
    },
    async getInvites(params?: { page?: number, per_page?: number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.INVITATIONS}`, {
            params: {
                page: params?.page,
                per_page: params?.per_page
            }
        });
    },

    /* ZONES */
    async getZones(params?: { page?: number, per_page?: number, patient_assignment_strategy?: UserInfo['assignment_strategy'], country_code?: string }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ZONES}`, {
            params: {
                page: params?.page,
                per_page: params?.per_page,
                patient_assignment_strategy: params?.patient_assignment_strategy,
                country_code: params?.country_code
            }
        });
    },
    async getZone(id: number | string) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ZONES}/${id}`);
    },
    async createZone(zone: Zone) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ZONES}`, zone);
    },
    async updateZone(zone: Zone) {
        return axios.put(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ZONES}/${zone.id!}`, zone);
    },
    async deleteZone(id: number | string) {
        return axios.delete(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ZONES}/${id}`);
    },
    async getZoneHCPs(params: { zone_id: number | string, page?: number, per_page?: number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ZONES}/${params.zone_id}${ENDPOINT.HCPS}`, {
            params: {
                page: params?.page,
            }
        });
    },
    async getAdverseEventConfigPrelogin() {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.EXTERNAL_API}${ENDPOINT.ADVERSE_EVENT_CONFIG}`, {
            params: { lang: i18n.global.locale }
        });
    },
    async getAdverseEventConfig(zone_id: number | string) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.ZONES}/${zone_id}${ENDPOINT.ADVERSE_EVENT_CONFIG}`, {
            params: { lang: i18n.global.locale }
        });
    },
    /* JOUrNEY SCHEMAS */
    async getJourneySchemas(params?: { page?: number, per_page?: number }) {
        return axios.get(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.EXPERIENCE_JOURNEY}${ENDPOINT.SCHEMAS}`, {
            params: {
                page: params?.page,
                per_page: params?.per_page,
            }
        });
    },
    async deleteRoleFromJourneySchema(params?: { schema_id: string | number, role_id: string | number }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.EXPERIENCE_JOURNEY}${ENDPOINT.SCHEMAS}/${params?.schema_id}${ENDPOINT.REMOVE_ROLE}`, {
                role_id: params?.role_id.toString()
        });
    },
    async storeRoleFromJourneySchema(params?: { schema_id: string | number, role_id: string | number }) {
        return axios.post(`${ENDPOINT.BASE_URL}${ENDPOINT.API}${ENDPOINT.EXPERIENCE_JOURNEY}${ENDPOINT.SCHEMAS}/${params?.schema_id}${ENDPOINT.ADD_ROLE}`, {
                role_id: params?.role_id.toString()
        });
    },

}

export type ExtraDefinitionEntity = 'patients';
export default API;